/* istanbul ignore file */
/**
 * Created by gboue on 8/24/22
 * File: ActionsRenderer
 * Project: MSP ©Palo Alto Networks
 * © Palo Alto Networks
 */

import { VFC, useContext } from 'react'
import { LABEL_OWNERS_ACTION_DELETE, LABEL_OWNERS_ACTION_VERIFY, LABEL_OWNERS_ACTION_VERIFY_INSTRUCTIONS, LABEL_OWNERS_STATUS_NA, TOOLTIP_DELETE } from '../../../../../constants/LabelConstants'
import AuthContext from '../../../../../../common/context/AuthContext'
import type { RowInstance } from '@panwds/react-table'
import { IDP_OWNER_STATUS, IDP_OWNER_TYPES } from '../../../../../constants/IAMConstants'
import { Body, Button, LoadingIcon, Select, Tooltip } from '@panwds/react-ui'
import { DATA_TEST_ACTIONS_GEAR, DATA_TEST_ACTIONS_TRASH } from '../../../../../../common/constants/TestingDataIds'
import { DeleteIcon, SettingsIcon } from '@panwds/icons'
import { caseInsensitiveIsEqual } from '../../../../../../common/utils/Utils'

/**
 *   Owners actions button table cell
 */

/**
 * type    status    same_user    icon
 dns     pending   yes          gear
 dns     pending   no           n/a
 dns     verified  yes          gear
 dns     verified  no           trash
 grant   verified  yes          gear
 grant   verified  no           trash
 */

const ActionsRenderer: VFC<{
  loading: boolean,
  fetchData: Function,
  value: { type: string, status: string, verificationString: string },
  onActionClick: Function,
  row: RowInstance<any>,
  domain: string,
  openDeleteModal: Function,
  closeDeleteModal: Function,
  isVerifyLoading: any
}> = ({
  loading,
  fetchData,
  value,
  onActionClick,
  row,
  domain,
  openDeleteModal,
  closeDeleteModal,
  isVerifyLoading
}) => {

  const { user } = useContext(AuthContext)

  const { type, status, verificationString } = value
  const { values: { owner_name: owner } } = row
  const isSameUser = caseInsensitiveIsEqual(owner, user?.email)
  const name = user?.email

  const items = []

  if (type !== IDP_OWNER_TYPES.OWNER_GRANT) {
    items.push(
      { value: LABEL_OWNERS_ACTION_VERIFY },
      { value: LABEL_OWNERS_ACTION_VERIFY_INSTRUCTIONS },
    )
  }

  if (isSameUser) {
    items.push(
      { value: LABEL_OWNERS_ACTION_DELETE },
    )
  }

  return (
    <div className="tw-flex tw-items-center">
      {isSameUser && (<Select
        addClassName="cmm-modal-select"
        defaultPlacement="left-start"
        {...{ items }}
        fitToItemWidth
        disabled={isVerifyLoading?.[owner] || loading}
        button={(
          <Button
            disabled={loading || isVerifyLoading?.[owner]}
            dataTestId={DATA_TEST_ACTIONS_GEAR}
            aria-label="custom-role-action"
            appearance="tertiary-clear"
            icon={isVerifyLoading?.[owner] ? (<LoadingIcon size="sm"/>) : <SettingsIcon size="sm"/>}
          />
        )}
        onChange={
          (value) => {
            onActionClick(value?.selectedItem?.value, { name, verification: { status, type, verificationString } })
          }
        }
      />)}
      {(!isSameUser && status === IDP_OWNER_STATUS.PENDING && type === IDP_OWNER_TYPES.DNS_TEXT) && (
        <Body addClassName="tw-pl-1.5">{LABEL_OWNERS_STATUS_NA}</Body>
      )}
      {(!isSameUser && ([IDP_OWNER_STATUS.VERIFIED, IDP_OWNER_STATUS.WARNING] as any[]).includes(status) && [IDP_OWNER_TYPES.DNS_TEXT, IDP_OWNER_TYPES.OWNER_GRANT].includes(type as any)) && (
        <Button
          data-testid={DATA_TEST_ACTIONS_TRASH}
          aria-label="owners-trash"
          appearance="tertiary-clear"
          disabled={loading || isVerifyLoading?.[owner]}
          style={{ background: 'transparent' }}
          icon={(
            <Tooltip label={TOOLTIP_DELETE}>
              <DeleteIcon/>
            </Tooltip>
          )}
          onClick={() => openDeleteModal({
            closeDeleteModal,
            owner,
            ownerName: owner,
            domain,
            fetchData,
            type,
          })}
        />
      )}
    </div>
  )
}

ActionsRenderer.propTypes = {}
export default ActionsRenderer

